import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Separator } from '@vaiu/ui/separator';
import { Badge } from '@vaiu/ui/badge';
import { 
  ArrowLeft, 
  Shield, 
  Mail, 
  FileText, 
  User, 
  Lock,
  Eye,
  Database,
  ExternalLink,
  Calendar,
  Globe,
  CreditCard,
  MessageSquare,
  MapPin,
  Bell,
  Smartphone,
  BarChart3,
  Users,
  AlertTriangle
} from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = "July 2025";
  const effectiveDate = "January 1, 2025";

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <Link to="/">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>
        <div className="flex items-center space-x-3 mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-gray-600">
              Last updated: {lastUpdated} | Effective: {effectiveDate}
            </p>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800">
            This Privacy Policy describes how Vaiu Global Inc. ("we", "us", or "our") collects, uses, and shares 
            your personal information when you use our tokenized payment platform and related services.
          </p>
        </div>
      </div>

      {/* Quick Navigation */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span>Quick Navigation</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <a href="#information-we-collect" className="block text-blue-600 hover:text-blue-800 text-sm">1. Information We Collect</a>
              <a href="#how-we-use-information" className="block text-blue-600 hover:text-blue-800 text-sm">2. How We Use Information</a>
              <a href="#third-party-services" className="block text-blue-600 hover:text-blue-800 text-sm">3. Third-Party Services</a>
              <a href="#data-sharing" className="block text-blue-600 hover:text-blue-800 text-sm">4. Data Sharing</a>
            </div>
            <div className="space-y-2">
              <a href="#your-rights" className="block text-blue-600 hover:text-blue-800 text-sm">5. Your Privacy Rights</a>
              <a href="#data-security" className="block text-blue-600 hover:text-blue-800 text-sm">6. Data Security</a>
              <a href="#international-transfers" className="block text-blue-600 hover:text-blue-800 text-sm">7. International Transfers</a>
              <a href="#contact-us" className="block text-blue-600 hover:text-blue-800 text-sm">8. Contact Us</a>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Link 
              to="/cookie-policy"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm mr-4"
            >
              <FileText className="w-4 h-4 mr-1" />
              Cookie Policy
            </Link>
            <Link 
              to="/cookie-settings"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
            >
              <Lock className="w-4 h-4 mr-1" />
              Cookie Settings
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* 1. Information We Collect */}
      <section id="information-we-collect" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Database className="w-5 h-5 text-blue-600" />
              <span>1. Information We Collect</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Personal Information */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center space-x-2">
                <User className="w-5 h-5 text-green-600" />
                <span>Personal Information</span>
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• <strong>Account Information:</strong> Name, email address, phone number, username</li>
                <li>• <strong>Identity Verification:</strong> Government-issued ID, date of birth, address</li>
                <li>• <strong>Profile Data:</strong> Profile picture, business information (for merchants)</li>
                <li>• <strong>Authentication Data:</strong> Passwords, PINs, security questions, biometric data</li>
              </ul>
            </div>

            {/* Financial Information */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center space-x-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                <span>Financial Information</span>
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• <strong>Payment Methods:</strong> Bank account details, credit/debit card information</li>
                <li>• <strong>Transaction Data:</strong> Payment history, token creation/redemption, balances</li>
                <li>• <strong>Merchant Data:</strong> Business registration, tax information, Stripe Connect details</li>
                <li>• <strong>KYC Information:</strong> Identity verification documents and status</li>
              </ul>
            </div>

            {/* Technical Information */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center space-x-2">
                <Smartphone className="w-5 h-5 text-purple-600" />
                <span>Technical Information</span>
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• <strong>Device Data:</strong> IP address, device type, operating system, browser information</li>
                <li>• <strong>Usage Data:</strong> Pages visited, features used, session duration, click patterns</li>
                <li>• <strong>Location Data:</strong> GPS coordinates (for address verification), time zone</li>
                <li>• <strong>Communication Data:</strong> Support messages, feedback, survey responses</li>
              </ul>
            </div>

            {/* Automatically Collected Information */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-orange-600" />
                <span>Automatically Collected Information</span>
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• <strong>Cookies and Tracking:</strong> Session cookies, preference cookies, analytics cookies</li>
                <li>• <strong>Log Data:</strong> Server logs, error logs, access logs, security events</li>
                <li>• <strong>Analytics Data:</strong> Google Analytics data, Firebase Analytics, usage patterns</li>
                <li>• <strong>Push Notification Data:</strong> Device tokens, notification preferences, delivery status</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 2. How We Use Information */}
      <section id="how-we-use-information" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">2. How We Use Your Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-6">
              
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold mb-2">Service Provision & Operations</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Process payments and transactions</li>
                  <li>• Create and manage payment tokens</li>
                  <li>• Maintain account balances and transaction history</li>
                  <li>• Provide customer support and troubleshooting</li>
                  <li>• Enable merchant features and payment acceptance</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="font-semibold mb-2">Security & Compliance</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Verify your identity and prevent fraud</li>
                  <li>• Comply with anti-money laundering (AML) regulations</li>
                  <li>• Monitor transactions for suspicious activity</li>
                  <li>• Maintain audit trails for regulatory compliance</li>
                  <li>• Protect against unauthorized access and cyber threats</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold mb-2">Communication & Support</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Send transaction confirmations and receipts</li>
                  <li>• Notify you of account activity and security alerts</li>
                  <li>• Provide customer service and technical support</li>
                  <li>• Send product updates and feature announcements</li>
                  <li>• Process feedback and survey responses</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="font-semibold mb-2">Analytics & Improvement</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Analyze usage patterns to improve our services</li>
                  <li>• Develop new features and enhance user experience</li>
                  <li>• Conduct market research and performance analysis</li>
                  <li>• Optimize system performance and reliability</li>
                  <li>• Generate aggregated insights and reports</li>
                </ul>
              </div>

              <div className="border-l-4 border-yellow-500 pl-4">
                <h3 className="font-semibold mb-2">Legal & Regulatory</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Comply with applicable financial services regulations</li>
                  <li>• Respond to legal requests and court orders</li>
                  <li>• Enforce our Terms of Service and policies</li>
                  <li>• Protect our rights and prevent legal violations</li>
                  <li>• Maintain records for regulatory audits</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 3. Third-Party Services */}
      <section id="third-party-services" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">3. Third-Party Services We Use</CardTitle>
            <CardDescription>
              We integrate with various third-party services to provide our platform functionality.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Payment Processors */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center space-x-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                <span>Payment & Financial Services</span>
              </h3>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">Stripe, Inc.</h4>
                  <p className="text-xs text-gray-600 mb-2">Payment processing, card tokenization, merchant services</p>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">PCI DSS Level 1</Badge>
                    <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" 
                       className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center">
                      Privacy Policy <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">PayPal Holdings, Inc.</h4>
                  <p className="text-xs text-gray-600 mb-2">Digital wallet integration, payment processing, payouts</p>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">PCI Compliant</Badge>
                    <a href="https://www.paypal.com/privacy" target="_blank" rel="noopener noreferrer" 
                       className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center">
                      Privacy Policy <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>
                </div>

                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">Plaid Inc.</h4>
                  <p className="text-xs text-gray-600 mb-2">Bank account linking and verification services</p>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">Bank-grade Security</Badge>
                    <a href="https://plaid.com/legal/privacy/" target="_blank" rel="noopener noreferrer" 
                       className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center">
                      Privacy Policy <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>
                </div>

                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">Bo Payments (VPPC)</h4>
                  <p className="text-xs text-gray-600 mb-2">Virtual prepaid card issuance and management</p>
                  <Badge variant="outline" className="text-xs">Licensed Financial Institution</Badge>
                </div>

                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">TangoCard</h4>
                  <p className="text-xs text-gray-600 mb-2">Gift card and rewards platform integration</p>
                  <a href="https://www.tangocard.com/privacy/" target="_blank" rel="noopener noreferrer" 
                     className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center">
                    Privacy Policy <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </div>
              </div>
            </div>

            {/* Google Services */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center space-x-2">
                <Globe className="w-5 h-5 text-green-600" />
                <span>Google Services</span>
              </h3>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">Google Analytics</h4>
                  <p className="text-xs text-gray-600 mb-2">Website analytics and user behavior tracking</p>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">Anonymized Data</Badge>
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" 
                       className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center">
                      Privacy Policy <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>
                </div>

                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">Google Maps Platform</h4>
                  <p className="text-xs text-gray-600 mb-2">Address verification, location services, mapping</p>
                  <Badge variant="outline" className="text-xs">Location Data</Badge>
                </div>

                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">Google OAuth 2.0</h4>
                  <p className="text-xs text-gray-600 mb-2">Social login and authentication services</p>
                  <Badge variant="outline" className="text-xs">OAuth Standard</Badge>
                </div>

                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">Firebase Cloud Messaging</h4>
                  <p className="text-xs text-gray-600 mb-2">Push notifications and real-time messaging</p>
                  <Badge variant="outline" className="text-xs">End-to-End Encrypted</Badge>
                </div>
              </div>
            </div>

            {/* Communication Services */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center space-x-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                <span>Communication Services</span>
              </h3>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">Twilio Inc.</h4>
                  <p className="text-xs text-gray-600 mb-2">SMS verification, phone number validation, notifications</p>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">SOC 2 Type II</Badge>
                    <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer" 
                       className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center">
                      Privacy Policy <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>
                </div>

                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-medium text-sm">Facebook OAuth</h4>
                  <p className="text-xs text-gray-600 mb-2">Social login and authentication services</p>
                  <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" 
                     className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center">
                    Privacy Policy <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </div>
              </div>
            </div>

            {/* Optional Analytics */}
            <div className="border rounded-lg p-4 border-yellow-200 bg-yellow-50">
              <h3 className="font-semibold mb-3 flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-yellow-600" />
                <span>Optional Analytics Services</span>
                <Badge variant="secondary" className="text-xs">Conditional</Badge>
              </h3>
              <p className="text-sm text-gray-700 mb-3">
                The following services may be used depending on your cookie preferences and configuration:
              </p>
              <div className="space-y-2 text-sm text-gray-700">
                <div>• <strong>Hotjar:</strong> Heatmaps and user session recordings (if enabled)</div>
                <div>• <strong>Mixpanel:</strong> Advanced user behavior analytics (if enabled)</div>
                <div>• <strong>Intercom:</strong> Customer support chat widget (if enabled)</div>
                <div>• <strong>Zendesk:</strong> Customer support ticketing system (if enabled)</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 4. Data Sharing */}
      <section id="data-sharing" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">4. When We Share Your Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-red-900 mb-1">Important</h3>
                  <p className="text-red-800 text-sm">
                    We do not sell, rent, or trade your personal information to third parties for marketing purposes.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold mb-2">Service Providers</h3>
                <p className="text-gray-700 text-sm">
                  We share data with trusted third-party service providers who help us operate our platform, 
                  process payments, and provide customer support. These providers are contractually bound to 
                  protect your data and use it only for specified purposes.
                </p>
              </div>
              
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold mb-2">Financial Partners</h3>
                <p className="text-gray-700 text-sm">
                  We share necessary financial information with payment processors, banks, and card networks 
                  to complete transactions, verify accounts, and comply with financial regulations.
                </p>
              </div>
              
              <div className="border-l-4 border-yellow-500 pl-4">
                <h3 className="font-semibold mb-2">Legal Requirements</h3>
                <p className="text-gray-700 text-sm">
                  We may disclose your information when required by law, court order, or government request, 
                  or to protect our rights, prevent fraud, or ensure public safety.
                </p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="font-semibold mb-2">Business Transfers</h3>
                <p className="text-gray-700 text-sm">
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                  to the new entity, subject to the same privacy protections.
                </p>
              </div>

              <div className="border-l-4 border-gray-500 pl-4">
                <h3 className="font-semibold mb-2">With Your Consent</h3>
                <p className="text-gray-700 text-sm">
                  We may share your information for other purposes with your explicit consent or at your direction.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 5. Your Privacy Rights */}
      <section id="your-rights" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">5. Your Privacy Rights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center space-x-2">
                  <Eye className="w-4 h-4 text-blue-600" />
                  <span>Access & Portability</span>
                </h3>
                <p className="text-sm text-gray-600">
                  Request access to your personal data and receive a copy in a portable format.
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-green-600" />
                  <span>Rectification</span>
                </h3>
                <p className="text-sm text-gray-600">
                  Request correction of inaccurate or incomplete personal data.
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center space-x-2">
                  <Lock className="w-4 h-4 text-red-600" />
                  <span>Erasure</span>
                </h3>
                <p className="text-sm text-gray-600">
                  Request deletion of your personal data under certain circumstances.
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-purple-600" />
                  <span>Restriction</span>
                </h3>
                <p className="text-sm text-gray-600">
                  Request restriction of processing under certain conditions.
                </p>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center space-x-2">
                  <User className="w-4 h-4 text-orange-600" />
                  <span>Objection</span>
                </h3>
                <p className="text-sm text-gray-600">
                  Object to processing for direct marketing or legitimate interests.
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center space-x-2">
                  <Users className="w-4 h-4 text-teal-600" />
                  <span>Withdrawal</span>
                </h3>
                <p className="text-sm text-gray-600">
                  Withdraw consent where processing is based on consent.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">How to Exercise Your Rights</h3>
              <div className="text-blue-800 text-sm space-y-2">
                <p>To exercise any of these rights, please contact us at:</p>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:privacy@vaiu.io" className="underline">privacy@vaiu.io</a>
                </div>
                <p>We will respond to your request within 30 days (or as required by applicable law).</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 6. Data Security */}
      <section id="data-security" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">6. Data Security & Protection</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              We implement comprehensive security measures to protect your personal information:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Encryption</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• TLS 1.3 for data in transit</li>
                  <li>• AES-256 encryption for data at rest</li>
                  <li>• End-to-end encryption for sensitive data</li>
                </ul>
              </div>
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Access Controls</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Multi-factor authentication</li>
                  <li>• Role-based access controls</li>
                  <li>• Regular access reviews</li>
                </ul>
              </div>
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Compliance</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• PCI DSS Level 1 compliance</li>
                  <li>• SOC 2 Type II certification</li>
                  <li>• GDPR compliance</li>
                </ul>
              </div>
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Monitoring</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 24/7 security monitoring</li>
                  <li>• Intrusion detection systems</li>
                  <li>• Regular security audits</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-medium text-green-900 mb-2">Data Breach Notification</h3>
              <p className="text-green-800 text-sm">
                In the unlikely event of a data breach, we will notify affected users and relevant authorities 
                within 72 hours as required by law, and provide guidance on protective measures.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 7. International Transfers */}
      <section id="international-transfers" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">7. International Data Transfers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Your information may be transferred to and processed in countries other than your own, including the United States.
            </p>
            
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold mb-2">Safeguards</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                  <li>• Adequacy decisions for certain countries</li>
                  <li>• Additional safeguards where required by law</li>
                  <li>• Regular assessment of transfer mechanisms</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold mb-2">Data Processing Locations</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• United States (primary data centers)</li>
                  <li>• European Union (for EU users)</li>
                  <li>• Canada (backup and redundancy)</li>
                  <li>• Third-party service provider locations as disclosed above</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 8. Data Retention */}
      <section className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>8. Data Retention</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              We retain your personal information for as long as necessary to provide our services and comply with legal obligations:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div>
                  <h4 className="font-medium">Account Information</h4>
                  <p className="text-sm text-gray-600">Retained while your account is active, plus 7 years after closure for regulatory compliance</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <h4 className="font-medium">Transaction Data</h4>
                  <p className="text-sm text-gray-600">Retained for 7 years as required by financial regulations</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                <div>
                  <h4 className="font-medium">Technical Logs</h4>
                  <p className="text-sm text-gray-600">Retained for 2 years for security and operational purposes</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                <div>
                  <h4 className="font-medium">Marketing Data</h4>
                  <p className="text-sm text-gray-600">Retained until you opt out or withdraw consent</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Cookie Management Section */}
      <section className="mb-8">
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-xl text-blue-700">Cookie Management</CardTitle>
            <CardDescription>
              Control how we use cookies and tracking technologies on our platform.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              We use cookies and similar technologies to enhance your experience. You have full control over these preferences:
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/cookie-policy">
                <Button variant="outline" className="w-full sm:w-auto">
                  <FileText className="w-4 h-4 mr-2" />
                  View Cookie Policy
                </Button>
              </Link>
              <Link to="/cookie-settings">
                <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700">
                  <Lock className="w-4 h-4 mr-2" />
                  Manage Cookie Settings
                </Button>
              </Link>
            </div>

            <div className="text-sm text-gray-600">
              <p>You can view detailed information about our cookie practices and manage your preferences at any time.</p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 9. Changes to Privacy Policy */}
      <section className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">9. Changes to This Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time to reflect changes in our practices, 
              technology, legal requirements, or other factors. We will notify you of any material changes by:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Posting an updated version on our website</li>
              <li>Updating the "Last Updated" date at the top of this policy</li>
              <li>Sending email notifications for significant changes</li>
              <li>Providing in-app notifications where appropriate</li>
            </ul>
            <p className="text-gray-700">
              We encourage you to review this policy periodically to stay informed about our privacy practices.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* Contact Section */}
      <section id="contact-us" className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Mail className="w-5 h-5" />
              <span>10. Contact Us</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium mb-3">Privacy Team</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-600" />
                    <a href="mailto:privacy@vaiu.io" className="text-blue-600 hover:text-blue-800">
                      privacy@vaiu.io
                    </a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-600" />
                    <a href="mailto:dpo@vaiu.io" className="text-blue-600 hover:text-blue-800">
                      dpo@vaiu.io (Data Protection Officer)
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium mb-3">Mailing Address</h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <p>Vaiu Global Inc.</p>
                  <p>Privacy Department</p>
                  <p>8 The Green #19779</p>
                  <p>Dover, DE 19901</p>
                  <p>United States</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">EU Representative</h3>
              <p className="text-blue-800 text-sm">
                If you are located in the European Union, you may also contact our EU representative 
                at <a href="mailto:eu-privacy@vaiu.io" className="underline">eu-privacy@vaiu.io</a>
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer Links */}
      <div className="text-center text-gray-600 text-sm">
        <p>
          Related policies: {' '}
          <Link to="/cookie-policy" className="text-blue-600 hover:text-blue-800">Cookie Policy</Link>
          {' • '}
          <Link to="/terms-of-service" className="text-blue-600 hover:text-blue-800">Terms of Service</Link>
          {' • '}
          <Link to="/help" className="text-blue-600 hover:text-blue-800">Help Center</Link>
        </p>
        <p className="mt-2 text-xs">
          © 2025 Vaiu Global Inc. All rights reserved. | Document Version 2.0
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage; 